import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  IconButton,
  Box,
  Button,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";

import { SideDrawer } from "../../../../components/SideDrawer";
import FilePreview from "../../components/FilePreview";
import {
  HAIRLINE,
  INK,
  INK_SECONDARY,
  SURFACE_MUTED,
  HOVER,
  FONT,
  RADIUS,
} from "../../../../theme/tokens";
import { LANGUAGE_LABEL } from "../../components/fileLanguage";
import StatusPill from "./StatusPill";

const PodStatusChip = ({ pod }) => {
  const { phase, container_statuses, deletion_timestamp } = pod;
  let color = "default";
  let text = phase || "Unknown";

  if (deletion_timestamp) {
    color = "warning";
    text = "Terminating";
  } else if (phase === "Running") {
    const allReady = container_statuses?.every((cs) => cs.ready);
    if (allReady) {
      color = "success";
      text = "Running";
    } else {
      color = "warning";
      text = "Running (Not Ready)";
    }
  } else if (phase === "Pending") {
    color = "warning";
  } else if (phase === "Succeeded") {
    color = "success";
    text = "Succeeded";
  } else if (phase === "Failed") {
    color = "error";
  }

  return <StatusPill label={text} color={color} />;
};
PodStatusChip.propTypes = {
  pod: PropTypes.object,
};

const MONO_FONT =
  "'Fira Mono', 'Menlo', 'Monaco', 'Consolas', 'Liberation Mono', monospace";

const CommandReaderDrawer = ({ command, open, podName, onClose }) => {
  const content = typeof command === "string" ? command : "";
  const lineCount = useMemo(() => {
    if (!content) return 0;
    return content.replace(/\r/g, "").split("\n").length;
  }, [content]);

  return (
    <SideDrawer
      open={open}
      onClose={onClose}
      title={`Pod Command (${podName || "..."})`}
    >
      <FilePreview
        fileName="command.sh"
        language="shell"
        languageLabel={LANGUAGE_LABEL.shell}
        content={content}
        lineCount={lineCount}
      />
    </SideDrawer>
  );
};

CommandReaderDrawer.propTypes = {
  command: PropTypes.string,
  open: PropTypes.bool,
  podName: PropTypes.string,
  onClose: PropTypes.func,
};

const copyTextToClipboard = async (text) => {
  if (!text || text === "N/A") return false;

  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Fall back to a temporary textarea when the async clipboard API is unavailable.
  }

  const textArea = document.createElement("textarea");
  textArea.value = text;
  textArea.setAttribute("readonly", "");
  textArea.style.position = "fixed";
  textArea.style.top = "-9999px";
  textArea.style.left = "-9999px";
  document.body.appendChild(textArea);
  textArea.select();

  try {
    return document.execCommand("copy");
  } catch {
    return false;
  } finally {
    document.body.removeChild(textArea);
  }
};

const LINE_HEIGHT_EM = 1.4;

const PodCommandCell = ({ copiedText, onCopy, podName, sshCmd }) => {
  const textRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const isCommandAvailable = sshCmd && sshCmd !== "N/A";
  const isCopied = copiedText === sshCmd;

  const previewText = useMemo(() => {
    if (!isCommandAvailable) return sshCmd;

    const logicalLines = sshCmd.replace(/\r/g, "").split("\n");
    if (logicalLines.length <= 2) {
      return sshCmd;
    }

    return `${logicalLines[0]}\n${logicalLines[1]} ...`;
  }, [isCommandAvailable, sshCmd]);

  useEffect(() => {
    if (!isCommandAvailable || !textRef.current) {
      setIsOverflowing(false);
      return undefined;
    }

    const node = textRef.current;
    const logicalLines = sshCmd.replace(/\r/g, "").split("\n");
    const updateOverflow = () =>
      setIsOverflowing(
        logicalLines.length > 2 || node.scrollHeight - node.clientHeight > 1,
      );

    updateOverflow();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateOverflow);
      return () => window.removeEventListener("resize", updateOverflow);
    }

    const observer = new ResizeObserver(updateOverflow);
    observer.observe(node);
    return () => observer.disconnect();
  }, [isCommandAvailable, sshCmd]);

  const handleCopyClick = async (event) => {
    event.stopPropagation();
    if (!isCommandAvailable) return;
    onCopy?.(sshCmd);
  };

  return (
    <>
      <TableCell sx={{ fontSize: FONT.md }}>
        <Box
          sx={{
            minHeight: 72,
            display: "grid",
            gridTemplateColumns: isCommandAvailable
              ? "minmax(0, 1fr) auto"
              : "minmax(0, 1fr)",
            alignItems: "stretch",
            gap: 1,
            fontFamily: MONO_FONT,
            fontSize: FONT.md,
            color: INK,
            backgroundColor: SURFACE_MUTED,
            border: `1px solid ${HAIRLINE}`,
            px: 1.5,
            py: 1,
            borderRadius: `${RADIUS.sm}px`,
          }}
        >
          <Box
            ref={textRef}
            sx={{
              overflow: "hidden",
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
              whiteSpace: "pre-wrap",
              overflowWrap: "anywhere",
              textOverflow: "ellipsis",
              lineHeight: `${LINE_HEIGHT_EM}em`,
              maxHeight: `${LINE_HEIGHT_EM * 2}em`,
              minWidth: 0,
              pr: 0.25,
            }}
          >
            {previewText}
          </Box>

          {isCommandAvailable && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                justifyContent: isOverflowing ? "space-between" : "flex-start",
                minWidth: 72,
              }}
            >
              <Tooltip title={isCopied ? "Copied!" : "Copy command"}>
                <Button
                  size="small"
                  variant="text"
                  onClick={handleCopyClick}
                  startIcon={<ContentCopyIcon fontSize="inherit" />}
                  sx={{
                    minWidth: 0,
                    px: 0.75,
                    py: 0.25,
                    color: INK_SECONDARY,
                    textTransform: "none",
                    whiteSpace: "nowrap",
                    "&:hover": {
                      color: INK,
                      backgroundColor: HOVER,
                    },
                  }}
                >
                  Copy
                </Button>
              </Tooltip>

              {isOverflowing && (
                <Tooltip title="View full command">
                  <Button
                    size="small"
                    variant="text"
                    onClick={(event) => {
                      event.stopPropagation();
                      setIsViewerOpen(true);
                    }}
                    startIcon={<VisibilityOutlinedIcon fontSize="inherit" />}
                    sx={{
                      minWidth: 0,
                      px: 0.75,
                      py: 0.25,
                      color: INK_SECONDARY,
                      textTransform: "none",
                      alignSelf: "flex-end",
                      whiteSpace: "nowrap",
                      "&:hover": {
                        color: INK,
                        backgroundColor: HOVER,
                      },
                    }}
                  >
                    View
                  </Button>
                </Tooltip>
              )}
            </Box>
          )}
        </Box>
      </TableCell>

      <CommandReaderDrawer
        command={sshCmd}
        open={isViewerOpen}
        podName={podName}
        onClose={() => setIsViewerOpen(false)}
      />
    </>
  );
};

PodCommandCell.propTypes = {
  copiedText: PropTypes.string,
  onCopy: PropTypes.func,
  podName: PropTypes.string,
  sshCmd: PropTypes.string,
};

const getPodDnsShortName = (hostName) => {
  if (!hostName || hostName === "N/A") return "N/A";
  return hostName.split(".")[0] || hostName;
};

const getWorkerAlias = (nodeIndex) =>
  nodeIndex !== null && nodeIndex !== undefined ? `worker-${nodeIndex}` : null;

const PodIndexCell = ({ nodeIndex }) => {
  const displayAlias = getWorkerAlias(nodeIndex) || "N/A";

  return (
    <TableCell sx={{ fontSize: FONT.md, textAlign: "center" }}>
      <Box
        sx={{
          minHeight: 72,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          columnGap: 0.5,
          rowGap: 0.25,
          flexWrap: "wrap",
        }}
      >
        <Box
          component="span"
          sx={{
            color: "text.secondary",
            fontSize: FONT.lg,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "100%",
          }}
        >
          {displayAlias}
        </Box>
      </Box>
    </TableCell>
  );
};

PodIndexCell.propTypes = {
  nodeIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

const PodHostCell = ({ copiedText, hostName, hostServer, onCopy }) => {
  const displayHostName = getPodDnsShortName(hostName);
  const displayHostServer = hostServer || "N/A";
  const isHostAvailable = displayHostName !== "N/A";
  const isCopied = copiedText === displayHostName;

  return (
    <TableCell sx={{ fontSize: FONT.md }}>
      <Box
        sx={{
          minHeight: 72,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: 0.5,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            minWidth: 0,
          }}
        >
          <Tooltip title={displayHostName}>
            <Box
              component="span"
              sx={{
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {displayHostName}
            </Box>
          </Tooltip>
          {isHostAvailable && (
            <Tooltip title={isCopied ? "Copied!" : "Copy hostname"}>
              <IconButton
                size="small"
                onClick={() => onCopy?.(displayHostName)}
                sx={{
                  color: INK_SECONDARY,
                  flexShrink: 0,
                  "&:hover": {
                    color: INK,
                    backgroundColor: HOVER,
                  },
                }}
              >
                <ContentCopyIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Tooltip title={displayHostServer}>
          <Box
            component="span"
            sx={{
              color: "text.secondary",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            Scheduled Server: {displayHostServer}
          </Box>
        </Tooltip>
      </Box>
    </TableCell>
  );
};

PodHostCell.propTypes = {
  copiedText: PropTypes.string,
  hostName: PropTypes.string,
  hostServer: PropTypes.string,
  onCopy: PropTypes.func,
};

export default function PodDetailsTable({
  pods = [],
  commandLabel = "SSH Command",
  onPodActionMenuOpen,
}) {
  const [copiedText, setCopiedText] = useState("");

  const handleCopy = async (textToCopy) => {
    const didCopy = await copyTextToClipboard(textToCopy);
    if (!didCopy) return;

    setCopiedText(textToCopy);
    window.setTimeout(() => {
      setCopiedText((currentText) =>
        currentText === textToCopy ? "" : currentText,
      );
    }, 2000);
  };

  return (
    <TableContainer
      sx={{
        border: `1px solid ${HAIRLINE}`, // light hairline outer border
        borderRadius: RADIUS.md,
        mb: 2, // bottom margin relative to parent container
        width: "100%",
        overflowX: "clip",
      }}
    >
      <Table
        size="small"
        aria-label="pod details"
        sx={{
          width: "100%",
          tableLayout: "fixed",
          "& td, & th": {
            border: `1px solid ${HAIRLINE}`, // thin hairline border for table cells
          },
        }}
      >
        {/* Table header: transparent background (prevents global dark header styles from applying) */}
        <TableHead
          sx={{
            backgroundColor: "transparent",
            "& .MuiTableCell-head": {
              backgroundColor: "transparent !important",
              color: "inherit",
              fontWeight: 600,
            },
          }}
        >
          <TableRow>
            <TableCell sx={{ width: "12%", textAlign: "center" }}>
              Index
            </TableCell>
            <TableCell sx={{ width: "14%", textAlign: "center" }}>
              Status
            </TableCell>
            <TableCell sx={{ width: "28%" }}>Hostname</TableCell>
            <TableCell sx={{ width: "36%" }}>{commandLabel}</TableCell>
            <TableCell sx={{ width: "10%", textAlign: "center" }}>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {pods.map((pod) => {
            const { sshCmd } = pod;
            return (
              <TableRow key={pod.name}>
                <PodIndexCell nodeIndex={pod.node_index} />

                <TableCell sx={{ fontSize: FONT.md, textAlign: "center" }}>
                  <PodStatusChip pod={pod} />
                </TableCell>

                <PodHostCell
                  copiedText={copiedText}
                  hostName={pod.pod_dns_name}
                  hostServer={pod.host_name}
                  onCopy={handleCopy}
                />

                <PodCommandCell
                  copiedText={copiedText}
                  onCopy={handleCopy}
                  podName={pod.name}
                  sshCmd={sshCmd || "N/A"}
                />

                <TableCell align="center">
                  <IconButton
                    aria-label="pod actions"
                    size="small"
                    onClick={(event) => onPodActionMenuOpen?.(event, pod)}
                  >
                    <MoreVertIcon fontSize="inherit" />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
          {(!pods || pods.length === 0) && (
            <TableRow>
              <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                No pods.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
PodDetailsTable.propTypes = {
  pods: PropTypes.array,
  commandLabel: PropTypes.string,
  onPodActionMenuOpen: PropTypes.func,
};
